import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ToasterService } from '@app/shared/services/toaster.service';
import {
  CopyResourceProjectsResult,
  FinanceService,
  ResourceInfoRow,
} from '../../finance.service';

/**
 * Data passed into the dialog. Comes from the Resource Info row whose
 * Copy icon was clicked — carries the source resource + the specific
 * assignment + project context so the modal title and field preview
 * can render without an extra fetch.
 */
export interface CopyResourceModalData {
  sourceResourceId: number;
  sourceAssignmentId: number;
  sourceResourceName: string;
  sourceEmployeeId?: string;
  sourceProjectName?: string;
  sourceProjectIdCode?: string;
  /** Field snapshot rendered as informational checkboxes in the modal
   *  per the Figma. Operator can deselect entries that shouldn't be
   *  copied; v1 honours the visible state for display only — the BE
   *  always clones the assignment + paired rate card. */
  fieldPreview: Array<{ label: string; value: string }>;
}

/**
 * Copy Resource Data modal — per-assignment clone of a single row's
 * assignment + rate-card period onto a target resource. Layout matches
 * the Figma: project-scoped title, source resource + project header,
 * Copy Information To dropdown, target resource search, deselect-able
 * field preview grid, Copy / Cancel footer.
 */
@Component({
  selector: 'app-copy-resource-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
  ],
  templateUrl: './copy-resource-modal.component.html',
  styleUrl: './copy-resource-modal.component.scss',
})
export class CopyResourceModalComponent implements OnInit, OnDestroy {
  /** "Copy Information To" dropdown — only "Single Resource" today;
   *  the type stays open so we can plug in a Multiple Resources flow
   *  later without redoing the bindings. */
  readonly copyModes: { value: 'single' | 'multiple'; label: string; disabled?: boolean }[] = [
    { value: 'single', label: 'Single Resource' },
    { value: 'multiple', label: 'Multiple Resources' },
  ];
  copyMode: 'single' | 'multiple' = 'single';

  searchText = '';
  searchResults: ResourceInfoRow[] = [];
  /** Targets the operator has picked. In Single mode capped at length 1
   *  (a new pick replaces); in Multiple mode unbounded with chip-style
   *  removal. */
  selectedTargets: ResourceInfoRow[] = [];

  /** Mirrors data.fieldPreview labels — true = include in copy preview
   *  (visual only in v1). Initialised to all-true on dialog open. */
  fieldSelections: Record<string, boolean> = {};

  searching = false;
  copying = false;

  private readonly searchInput$ = new Subject<string>();
  private readonly subs = new Subscription();

  constructor(
    private readonly dialogRef: MatDialogRef<CopyResourceModalComponent, CopyResourceProjectsResult | null>,
    @Inject(MAT_DIALOG_DATA) public readonly data: CopyResourceModalData,
    private readonly financeService: FinanceService,
    private readonly toaster: ToasterService,
  ) {}

  ngOnInit(): void {
    // Default every preview row to "included".
    this.data.fieldPreview.forEach((f) => (this.fieldSelections[f.label] = true));

    this.subs.add(
      this.searchInput$
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((needle) => this.runSearch(needle)),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /** Title shown at the top of the modal — "Copy {Project Name} Information To"
   *  when the source row has a project, else a sensible fallback. */
  get title(): string {
    const project = this.data.sourceProjectName ?? '';
    return project
      ? `Copy ${project} Information To`
      : 'Copy Resource Data';
  }

  onSearchChange(): void {
    this.searchInput$.next(this.searchText);
    // In single mode typing a fresh search clears the prior pick so
    // operator can replace it. In multi mode chips persist while they
    // continue searching for more targets.
    if (this.copyMode === 'single' && this.selectedTargets.length > 0) {
      this.selectedTargets = [];
    }
  }

  /** Mode switch (Single ↔ Multiple) — keep the first picked target
   *  if there is one; drop the rest. Defensive so flipping modes
   *  doesn't strand selections that the next mode can't use. */
  onModeChange(): void {
    if (this.copyMode === 'single' && this.selectedTargets.length > 1) {
      this.selectedTargets = [this.selectedTargets[0]];
    }
  }

  private runSearch(needle: string): void {
    const q = (needle ?? '').trim();
    if (q.length < 1) {
      this.searchResults = [];
      return;
    }
    this.searching = true;
    this.subs.add(
      this.financeService
        .listResourceInfo({ search: q, page: 0, size: 15 })
        .subscribe({
          next: (page) => {
            // Show every match — the BE provisions a FinanceResource
            // for new-hire targets on the fly, so non-provisioned rows
            // are still valid copy candidates now. Filter by
            // employeeId everywhere (resourceId is null for new
            // hires). Exclude the source row + anything already
            // picked.
            const alreadyPicked = new Set(this.selectedTargets.map((t) => t.employeeId));
            this.searchResults = (page.rows ?? [])
              .filter((r) => r.resourceId !== this.data.sourceResourceId)
              .filter((r) => !alreadyPicked.has(r.employeeId));
            this.searching = false;
          },
          error: (err) => {
            console.error('Target search failed', err);
            this.searchResults = [];
            this.searching = false;
          },
        }),
    );
  }

  /** Every row with an employeeId is a valid copy target — the BE
   *  provisions a FinanceResource on the fly for new-hire rows that
   *  exist as Employee records but don't yet have a finance row.
   *  Rows without employeeId (shouldn't happen for the Resource Info
   *  join, defensive) stay un-pickable. */
  canPick(row: ResourceInfoRow): boolean {
    return !!row.employeeId;
  }

  /** Used by mat-autocomplete to render the picked row's display value
   *  back into the input. Receiving anything other than a row (e.g.
   *  a raw search string) just echoes it. */
  displayResource = (row: ResourceInfoRow | string | null): string => {
    if (!row) return '';
    if (typeof row === 'string') return row;
    return row.resourceName ?? '';
  };

  /** mat-autocomplete (optionSelected) handler — fires when the operator
   *  picks an entry from the dropdown via click or keyboard. */
  onAutocompleteSelected(event: MatAutocompleteSelectedEvent): void {
    this.pickTarget(event.option.value as ResourceInfoRow);
  }

  pickTarget(row: ResourceInfoRow): void {
    if (!this.canPick(row)) return;
    if (this.copyMode === 'single') {
      // Single mode: a new pick replaces the prior selection; search
      // input shows the chosen name and dropdown closes.
      this.selectedTargets = [row];
      this.searchText = row.resourceName;
      this.searchResults = [];
    } else {
      // Multi mode: append to chip set, clear the search so they can
      // type the next name; dropdown re-opens on the next keystroke.
      // Dedupe by employeeId — new-hire targets share resourceId=null
      // so the prior resourceId-based dedupe would have grouped them
      // into a single chip incorrectly.
      if (this.selectedTargets.some((t) => t.employeeId === row.employeeId)) return;
      this.selectedTargets = [...this.selectedTargets, row];
      this.searchText = '';
      this.searchResults = [];
    }
  }

  removeTarget(row: ResourceInfoRow): void {
    this.selectedTargets = this.selectedTargets.filter((t) => t.employeeId !== row.employeeId);
  }

  clearAllSelections(): void {
    this.selectedTargets = [];
    this.searchText = '';
    this.searchResults = [];
  }

  confirmCopy(): void {
    const targets = this.selectedTargets.filter((t) => !!t.employeeId);
    if (targets.length === 0) {
      this.toaster.showError('Please pick at least one target resource', 'Validation error');
      return;
    }
    this.copying = true;

    // One HTTP call per target via the employeeId-based endpoint —
    // the BE provisions a FinanceResource on the fly for new-hire
    // targets so the FE doesn't need to branch on whether resourceId
    // is set. forkJoin runs them in parallel; if any individual call
    // throws, the whole pipeline errors and we skip the success toast.
    const calls = targets.map((t) =>
      this.financeService.copyAssignmentToEmployee(this.data.sourceAssignmentId, t.employeeId),
    );
    this.subs.add(
      forkJoin(calls).subscribe({
        next: (results) => {
          this.copying = false;
          const totals = results.reduce(
            (acc, r) => ({
              copied: acc.copied + r.assignmentsCopied,
              rateCards: acc.rateCards + r.rateCardsCopied,
              skipped: acc.skipped + r.assignmentsSkipped,
            }),
            { copied: 0, rateCards: 0, skipped: 0 },
          );

          if (totals.copied === 0 && totals.skipped > 0) {
            this.toaster.showWarning(
              `Skipped — every target already has this project assignment at the same start date.`,
              'Nothing copied',
            );
          } else {
            const parts: string[] = [];
            parts.push(`${totals.copied} assignment${totals.copied === 1 ? '' : 's'}`);
            if (totals.rateCards > 0) {
              parts.push(`${totals.rateCards} rate card${totals.rateCards === 1 ? '' : 's'}`);
            }
            if (totals.skipped > 0) {
              parts.push(`${totals.skipped} skipped`);
            }
            this.toaster.showSuccess(`Copied — ${parts.join(', ')}`, 'Success');
          }
          // Use the first result for the dialog close payload; the
          // FinanceDetails component only checks truthy-ness to decide
          // whether to refresh the table.
          this.dialogRef.close(results[0] ?? null);
        },
        error: (err) => {
          this.copying = false;
          const message =
            err?.error?.reason || err?.error?.message || 'Failed to copy resource data';
          console.error('Copy assignment failed', err);
          this.toaster.showError(message, 'Copy failed');
        },
      }),
    );
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
